import { useEffect, useState } from 'react';
import { useMessengerV2ControllerContext } from '../messenger-v2/MessengerV2ControllerContext.jsx';
import { syncMessengerV2NotificationHistoryKeys } from '../messenger-v2/runtime/messengerNotificationCrypto.js';
import { selectMessengerV2UnreadCount } from '../messenger-v2/runtime/messengerUnreadState.js';
import {
  formatUnreadDocumentTitle,
  normalizeUnreadCount,
  stripUnreadTitlePrefix,
  syncInstalledAppBadge,
  syncUnreadFavicon,
} from './appAttention.js';

export const MessengerAppAttention = () => {
  const controller = useMessengerV2ControllerContext();
  const canonicalUnreadCount = selectMessengerV2UnreadCount(controller?.state);
  const [workerUnreadCount, setWorkerUnreadCount] = useState(null);
  const unreadCount = workerUnreadCount ?? canonicalUnreadCount;

  useEffect(() => {
    void syncMessengerV2NotificationHistoryKeys();
  }, []);

  useEffect(() => {
    setWorkerUnreadCount(null);
  }, [canonicalUnreadCount]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;
    let visibleResetTimer = null;
    const handleWorkerMessage = (event) => {
      if (event?.data?.type !== 'MESSENGER_V2_UNREAD_COUNT') return;
      setWorkerUnreadCount(normalizeUnreadCount(event.data.unreadCount));
    };
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(visibleResetTimer);
      visibleResetTimer = window.setTimeout(() => setWorkerUnreadCount(null), 1500);
    };

    navigator.serviceWorker.addEventListener('message', handleWorkerMessage);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearTimeout(visibleResetTimer);
      navigator.serviceWorker.removeEventListener('message', handleWorkerMessage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const applyTitle = () => {
      const nextTitle = formatUnreadDocumentTitle(document.title, unreadCount);
      if (document.title !== nextTitle) document.title = nextTitle;
    };

    applyTitle();
    const titleNode = document.querySelector('title');
    const observer = titleNode ? new MutationObserver(applyTitle) : null;
    observer?.observe(titleNode, { childList: true, characterData: true, subtree: true });
    return () => {
      observer?.disconnect();
      document.title = stripUnreadTitlePrefix(document.title);
    };
  }, [unreadCount]);

  useEffect(() => {
    void syncUnreadFavicon(unreadCount);
  }, [unreadCount]);

  useEffect(
    () => () => {
      void syncUnreadFavicon(0);
    },
    [],
  );

  useEffect(() => {
    void syncInstalledAppBadge(unreadCount);
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.ready) return;
    void navigator.serviceWorker.ready
      .then((registration) => {
        registration.active?.postMessage?.({
          type: 'MESSENGER_V2_SYNC_UNREAD_BADGE',
          unreadCount,
        });
      })
      .catch(() => null);
  }, [unreadCount]);

  return null;
};

export default MessengerAppAttention;
